// FinTrack — Početno stanje (opening balances)
// Import, pregled, edit, brisanje, multi-select, filtriranje, QuickBooks export, zaključi period

import { state } from './state.js';
import { sb } from './supabase.js';
import { toast, svgIcon, openEditModal, showLoading, hideLoading, clMdl, showConfirm } from './ui.js';
import { fNum } from './utils.js';
import XLSX from 'xlsx-js-style';

let psData = [];
let basePsData = [];
let psSortCol = 'konto', psSortAsc = true;
let psSelectedKontos = new Set(); // for dropdown filter (empty = all)

export async function loadPocetnoStanje() {
  showLoading();
  try {
    const { data, error } = await sb.from('pocetno_stanje').select('*').order('id', { ascending: true });
    if (error) throw error;
    basePsData = data || [];
    psData = basePsData;
  } catch (e) { basePsData = []; psData = []; toast('Greška pri učitavanju početnog stanja', 'err'); }
  hideLoading();
  buildPsKontoDropdown();
  renderPocetnoStanje();
}

function sortPsData(arr) {
  // Grupiši analitike pod parent koristeći opis polje (parent konto) ili ID redosled kao fallback
  const parentMap = new Map(); // parent konto → { p: parent row, ch: [analitike] }
  const orphans = []; // analitike bez parenta

  // Prvo napravi mapu parenta (S redovi)
  arr.filter(p => p.tip !== 'A' && p.tip !== 'K').forEach(p => {
    parentMap.set((p.konto || '').trim(), { p, ch: [] });
  });

  // Grupiši analitike pod parent: koristi opis polje ako postoji, inače ID redosled
  const analytics = arr.filter(p => p.tip === 'A' || p.tip === 'K');
  analytics.forEach(a => {
    const parentKonto = (a.opis || '').trim();
    if (parentKonto && parentMap.has(parentKonto)) {
      parentMap.get(parentKonto).ch.push(a);
    } else {
      // Fallback: traži parent po ID redosledu (prethodni S red sa nižim ID)
      let bestParent = null;
      for (const [k, g] of parentMap) {
        if (g.p.id < a.id && (!bestParent || g.p.id > bestParent.p.id)) bestParent = g;
      }
      if (bestParent) bestParent.ch.push(a);
      else orphans.push(a);
    }
  });

  // Sortiraj grupe po parent-ovom polju
  const groups = [...parentMap.values()];
  groups.sort((a, b) => {
    let va = a.p[psSortCol] ?? '', vb = b.p[psSortCol] ?? '';
    if (psSortCol === 'duguje' || psSortCol === 'potrazuje') {
      va = parseFloat(va) || 0; vb = parseFloat(vb) || 0;
      return psSortAsc ? va - vb : vb - va;
    }
    va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
    return psSortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
  });

  // Flatten: parent pa njegove analitike
  const result = [];
  groups.forEach(g => { result.push(g.p); result.push(...g.ch); });
  result.push(...orphans);
  return result;
}

// ============================================================
// KONTO DROPDOWN FILTER
// ============================================================
function buildPsKontoDropdown() {
  const list = document.getElementById('psKontoList');
  if (!list) return;
  const kontos = [...new Set(psData.filter(p => p.tip !== 'X').map(p => (p.konto || '').trim()))].sort((a, b) => a.localeCompare(b));
  list.textContent = '';
  // Select all / Deselect all
  const allRow = document.createElement('div');
  allRow.style.cssText = 'padding:6px 10px;display:flex;gap:6px;border-bottom:1px solid var(--border)';
  const btnAll = document.createElement('button'); btnAll.className = 'bsm'; btnAll.style.cssText = 'font-size:10px;padding:2px 8px';
  btnAll.textContent = 'Sva konta'; btnAll.onclick = () => { psSelectedKontos.clear(); updatePsKontoBtn(); renderPocetnoStanje(); };
  const btnNone = document.createElement('button'); btnNone.className = 'bsm'; btnNone.style.cssText = 'font-size:10px;padding:2px 8px';
  btnNone.textContent = 'Poništi'; btnNone.onclick = () => { psSelectedKontos.clear(); updatePsKontoBtn(); renderPocetnoStanje(); };
  allRow.appendChild(btnAll); allRow.appendChild(btnNone);
  list.appendChild(allRow);

  kontos.forEach(k => {
    const row = document.createElement('label');
    row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:4px 10px;cursor:pointer;font-size:12px';
    row.dataset.konto = k.toLowerCase();
    const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = !psSelectedKontos.size || psSelectedKontos.has(k);
    cb.style.cssText = 'flex-shrink:0';
    cb.onchange = () => {
      if (cb.checked) psSelectedKontos.add(k); else psSelectedKontos.delete(k);
      // If all are selected, clear set (means "all")
      if (psSelectedKontos.size === kontos.length) psSelectedKontos.clear();
      updatePsKontoBtn(); renderPocetnoStanje();
    };
    const sp = document.createElement('span'); sp.style.fontFamily = 'var(--mn)'; sp.textContent = k;
    const nm = psData.find(p => p.konto?.trim() === k);
    const sp2 = document.createElement('span'); sp2.style.cssText = 'color:var(--tx3);font-size:11px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
    sp2.textContent = nm ? (nm.naziv_konta || '').substring(0, 25) : '';
    row.appendChild(cb); row.appendChild(sp); row.appendChild(sp2);
    list.appendChild(row);
  });
}

function updatePsKontoBtn() {
  const btn = document.getElementById('psKontoBtn');
  if (!btn) return;
  btn.textContent = psSelectedKontos.size ? psSelectedKontos.size + ' konta izabrano' : 'Sva konta';
}

export function togglePsKontoDrop() {
  const drop = document.getElementById('psKontoDrop');
  if (!drop) return;
  drop.style.display = drop.style.display === 'none' ? '' : 'none';
  if (drop.style.display !== 'none') {
    document.getElementById('psKontoFilter')?.focus();
    // Close on outside click
    setTimeout(() => document.addEventListener('click', closePsKontoDrop, { once: true }), 10);
  }
}

function closePsKontoDrop(e) {
  const drop = document.getElementById('psKontoDrop');
  if (drop && !drop.contains(e?.target) && e?.target?.id !== 'psKontoBtn') drop.style.display = 'none';
}

export function filterPsKontoDrop() {
  const q = (document.getElementById('psKontoFilter')?.value || '').toLowerCase();
  document.querySelectorAll('#psKontoList label').forEach(l => {
    l.style.display = !q || (l.dataset.konto || '').includes(q) ? '' : 'none';
  });
}

// ============================================================
// RENDER
// ============================================================
export function renderPocetnoStanje() {
  const thead = document.getElementById('psHead');
  const tbody = document.getElementById('psBody');
  const tfoot = document.getElementById('psFoot');
  const empty = document.getElementById('psEmpty');
  const count = document.getElementById('psCount');
  if (!tbody) return;
  tbody.textContent = '';

  // Sortable headers with checkbox
  if (thead) {
    thead.textContent = '';
    const cols = [
      { key: null, label: '', isCheck: true },
      { key: 'konto', label: 'Konto' }, { key: 'naziv_konta', label: 'Naziv konta' },
      { key: 'duguje', label: 'Duguje' }, { key: 'potrazuje', label: 'Potražuje' },
      { key: 'tip', label: 'Tip' }, { key: null, label: '' }
    ];
    cols.forEach(c => {
      const th = document.createElement('th');
      if (c.isCheck) {
        th.style.width = '30px';
        const ck = document.createElement('input'); ck.type = 'checkbox'; ck.id = 'psSelectAll';
        ck.onchange = function () { document.querySelectorAll('.ps-ck').forEach(cb => { cb.checked = this.checked; }); updatePsDelBtn(); };
        th.appendChild(ck);
      } else {
        th.textContent = c.label;
        if (c.key) {
          th.className = 'sortable' + (psSortCol === c.key ? (psSortAsc ? ' asc' : ' desc') : '');
          const si = document.createElement('span'); si.className = 'sort-icon';
          si.textContent = psSortCol === c.key ? (psSortAsc ? '\u25B2' : '\u25BC') : '\u25B4';
          th.appendChild(si);
          th.onclick = () => { if (psSortCol === c.key) psSortAsc = !psSortAsc; else { psSortCol = c.key; psSortAsc = true; } renderPocetnoStanje(); };
        }
        if (c.key === 'duguje' || c.key === 'potrazuje') th.style.textAlign = 'right';
      }
      thead.appendChild(th);
    });
  }

  // Filter
  const q = (document.getElementById('psSearch')?.value || '').toLowerCase();
  let filtered = psData.filter(p => p.tip !== 'X');
  if (psSelectedKontos.size) filtered = filtered.filter(p => psSelectedKontos.has((p.konto || '').trim()));
  if (q) filtered = filtered.filter(p => (p.konto || '').toLowerCase().includes(q) || (p.naziv_konta || '').toLowerCase().includes(q));
  filtered = sortPsData(filtered);

  // UKUPNO = zbir parent vrednosti (parent sa analitikama koristi zbir analitika, bez = sopstvenu)
  // Analitike se NE računaju direktno jer su već uračunate u parent zbir
  // Ovo se računa POSLE analSums mape (dole), pa koristimo placeholder
  let totalD = 0, totalC = 0;
  if (count) count.textContent = filtered.length + ' stavki';

  if (!filtered.length) { if (empty) empty.style.display = 'block'; if (tfoot) tfoot.textContent = ''; return; }
  if (empty) empty.style.display = 'none';

  // Napravi mapu: parent konto → zbir analitika iz SORTIRANOG redosleda
  // (sort već pravilno grupiše A/K pod parent — koristimo taj redosled)
  const analSums = new Map();
  {
    let curPK = null;
    filtered.forEach(p => {
      const isChild = p.tip === 'A' || p.tip === 'K';
      if (!isChild) {
        curPK = (p.konto || '').trim();
      } else if (curPK) {
        const s = analSums.get(curPK) || { d: 0, p: 0, n: 0 };
        s.d += parseFloat(p.duguje) || 0;
        s.p += parseFloat(p.potrazuje) || 0;
        s.n++;
        analSums.set(curPK, s);
      }
    });
  }

  // UKUPNO: parent sa analitikama = zbir analitika, parent bez = sopstvena vrednost
  filtered.forEach(p => {
    if (p.tip === 'A' || p.tip === 'K') return;
    const pk = (p.konto || '').trim();
    const aSum = analSums.get(pk);
    totalD += aSum ? aSum.d : (parseFloat(p.duguje) || 0);
    totalC += aSum ? aSum.p : (parseFloat(p.potrazuje) || 0);
  });

  // Data rows sa KLASA subtotalima
  let curClass = null, clsD = 0, clsP = 0;
  const klasaStyle = 'font-weight:700;font-size:13px;background:linear-gradient(90deg,rgba(201,168,76,.18),rgba(201,168,76,.06));border-top:2px solid var(--acc);border-bottom:2px solid var(--acc)';

  function renderKlasaRow(cls) {
    if (cls === null) return;
    const tr = document.createElement('tr');
    const t0 = document.createElement('td'); t0.style.cssText = klasaStyle;
    const t1 = document.createElement('td'); t1.style.cssText = klasaStyle + ';font-family:var(--mn);color:var(--acc)';
    const t2 = document.createElement('td'); t2.style.cssText = klasaStyle + ';color:var(--acc)'; t2.textContent = 'KLASA ' + cls;
    const t3 = document.createElement('td'); t3.style.cssText = klasaStyle + ';text-align:right;font-family:var(--mn);color:var(--dng)'; if (clsD > 0) t3.textContent = fNum(clsD);
    const t4 = document.createElement('td'); t4.style.cssText = klasaStyle + ';text-align:right;font-family:var(--mn);color:var(--suc)'; if (clsP > 0) t4.textContent = fNum(clsP);
    const t5 = document.createElement('td'); t5.style.cssText = klasaStyle;
    const t6 = document.createElement('td'); t6.style.cssText = klasaStyle;
    tr.appendChild(t0); tr.appendChild(t1); tr.appendChild(t2); tr.appendChild(t3); tr.appendChild(t4); tr.appendChild(t5); tr.appendChild(t6);
    tbody.appendChild(tr);
  }

  filtered.forEach(p => {
    const isA = p.tip === 'A' || p.tip === 'K';
    const pk = (p.konto || '').trim();

    // KLASA subtotal kad se klasa promeni (samo za parent redove)
    if (!isA && pk) {
      const cls = pk.charAt(0);
      if (curClass !== null && cls !== curClass) { renderKlasaRow(curClass); clsD = 0; clsP = 0; }
      curClass = cls;
    }

    // Sabiraj u KLASA total (samo parente, ne analitike — one su već uračunate)
    if (!isA) {
      const aSum2 = analSums.has(pk) ? analSums.get(pk) : null;
      clsD += aSum2 ? aSum2.d : (parseFloat(p.duguje) || 0);
      clsP += aSum2 ? aSum2.p : (parseFloat(p.potrazuje) || 0);
    }

    const tr = document.createElement('tr');
    if (isA) tr.style.cssText = 'background:rgba(232,184,77,.06)';

    // Checkbox
    const tdCk = document.createElement('td');
    const ck = document.createElement('input'); ck.type = 'checkbox'; ck.className = 'ps-ck'; ck.value = p.id; ck.onchange = updatePsDelBtn;
    tdCk.appendChild(ck);

    const td0 = document.createElement('td');
    td0.style.cssText = 'font-family:var(--mn);font-weight:600' + (isA ? ';padding-left:30px;font-style:italic' : '');
    td0.textContent = (isA ? '\u2514 ' : '') + pk;

    const td1 = document.createElement('td');
    td1.style.cssText = (isA ? 'font-style:italic' : 'font-weight:600');
    td1.textContent = (p.naziv_konta || '').trim();

    const numBase = 'text-align:right;font-family:var(--mn)';
    const aSum = !isA && analSums.has(pk) ? analSums.get(pk) : null;
    const showD = aSum ? aSum.d : parseFloat(p.duguje || 0);
    const showP = aSum ? aSum.p : parseFloat(p.potrazuje || 0);

    const td2 = document.createElement('td'); td2.style.cssText = numBase;
    if (showD > 0) { td2.textContent = fNum(showD); td2.style.color = 'var(--dng)'; }

    const td3 = document.createElement('td'); td3.style.cssText = numBase;
    if (showP > 0) { td3.textContent = fNum(showP); td3.style.color = 'var(--suc)'; }

    const td4 = document.createElement('td'); td4.style.cssText = 'font-size:11px;color:var(--tx3)';
    const tipMap = { S: 'Sintetika', A: 'Analitika', K: 'Komitent', aktiva: 'Aktiva', kapital: 'Kapital', obaveze: 'Obaveze', rezultat: 'Rezultat' };
    td4.textContent = aSum ? tipMap[p.tip] + ' (\u03A3' + aSum.n + ')' : (tipMap[p.tip] || p.tip || '—');

    const td5 = document.createElement('td'); td5.style.cssText = 'white-space:nowrap';
    const acts = document.createElement('div'); acts.style.cssText = 'display:flex;gap:4px;justify-content:flex-end';
    if (!isA) {
      const btnAn = document.createElement('button'); btnAn.className = 'bsm'; btnAn.style.cssText = 'font-size:9px;padding:2px 6px;background:var(--acc);color:#000;border-color:var(--acc)';
      btnAn.textContent = '+ Analitika';
      btnAn.onclick = () => addAnalitika(p);
      acts.appendChild(btnAn);
    }
    const btnEd = document.createElement('button'); btnEd.className = 'bsm'; btnEd.style.cssText = 'font-size:9px;padding:2px 6px';
    btnEd.textContent = 'Izmeni';
    btnEd.onclick = () => editPocetnoStanje(p);
    const btnDel = document.createElement('button'); btnDel.className = 'bsm'; btnDel.style.cssText = 'font-size:9px;padding:2px 6px;color:var(--dng);border-color:var(--dng)';
    btnDel.textContent = 'Obriši';
    btnDel.onclick = () => deletePocetnoStanje(p);
    acts.appendChild(btnEd); acts.appendChild(btnDel);
    td5.appendChild(acts);

    tr.appendChild(tdCk); tr.appendChild(td0); tr.appendChild(td1); tr.appendChild(td2); tr.appendChild(td3); tr.appendChild(td4); tr.appendChild(td5);
    tbody.appendChild(tr);
  });
  // Poslednja klasa
  renderKlasaRow(curClass);

  // Footer totals
  if (tfoot) {
    tfoot.textContent = '';
    const fStyle = 'font-weight:700;font-size:13px;background:rgba(232,184,77,.08);border-top:2px solid var(--acc)';
    const tdE = document.createElement('td'); tdE.style.cssText = fStyle;
    const tdK = document.createElement('td'); tdK.style.cssText = fStyle;
    const tdN = document.createElement('td'); tdN.style.cssText = fStyle + ';font-weight:700'; tdN.textContent = 'UKUPNO';
    const tdD = document.createElement('td'); tdD.style.cssText = fStyle + ';text-align:right;font-family:var(--mn);color:var(--dng)'; tdD.textContent = fNum(totalD);
    const tdP = document.createElement('td'); tdP.style.cssText = fStyle + ';text-align:right;font-family:var(--mn);color:var(--suc)'; tdP.textContent = fNum(totalC);
    const tdT = document.createElement('td'); tdT.style.cssText = fStyle;
    const tdA = document.createElement('td'); tdA.style.cssText = fStyle;
    tfoot.appendChild(tdE); tfoot.appendChild(tdK); tfoot.appendChild(tdN); tfoot.appendChild(tdD); tfoot.appendChild(tdP); tfoot.appendChild(tdT); tfoot.appendChild(tdA);
  }
}

function updatePsDelBtn() {
  const checked = document.querySelectorAll('.ps-ck:checked');
  const btn = document.getElementById('psDelSelBtn');
  if (btn) {
    btn.style.display = checked.length ? '' : 'none';
    btn.textContent = 'Obriši selektovane (' + checked.length + ')';
  }
}

// ============================================================
// ADD / EDIT / DELETE
// ============================================================
export function addPsEntry() {
  openEditModal('Nova stavka početnog stanja', [
    { label: 'Konto', value: '', key: 'konto' },
    { label: 'Naziv konta', value: '', key: 'naziv_konta' },
    { label: 'Duguje', value: '0', key: 'duguje', type: 'number', step: '0.01' },
    { label: 'Potražuje', value: '0', key: 'potrazuje', type: 'number', step: '0.01' },
    { label: 'Tip (S/A/K)', value: 'S', key: 'tip' }
  ], async (vals) => {
    if (!vals.konto) { toast('Unesite konto', 'err'); return; }
    try {
      const { data, error } = await sb.from('pocetno_stanje').insert({
        konto: vals.konto.trim(),
        naziv_konta: vals.naziv_konta.trim(),
        duguje: parseFloat(vals.duguje) || 0,
        potrazuje: parseFloat(vals.potrazuje) || 0,
        tip: vals.tip || 'S'
      }).select();
      if (error) throw error;
      if (data?.[0]) { basePsData.push(data[0]); psData = basePsData; }
      buildPsKontoDropdown();
      renderPocetnoStanje();
      toast('Stavka dodana: ' + vals.konto);
    } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  });
}

function addAnalitika(parent) {
  const parentKonto = (parent.konto || '').trim();
  openEditModal('Dodaj analitiku za konto ' + parentKonto + ' (' + (parent.naziv_konta || '').substring(0, 25) + ')', [
    { label: 'Šifra analitike', value: '', key: 'konto' },
    { label: 'Naziv (komitent/opis)', value: '', key: 'naziv_konta' },
    { label: 'Duguje', value: '0', key: 'duguje', type: 'number', step: '0.01' },
    { label: 'Potražuje', value: '0', key: 'potrazuje', type: 'number', step: '0.01' }
  ], async (vals) => {
    if (!vals.konto) { toast('Unesite šifru analitike', 'err'); return; }
    try {
      const { error } = await sb.from('pocetno_stanje').insert({
        konto: vals.konto.trim(),
        naziv_konta: vals.naziv_konta.trim(),
        duguje: parseFloat(vals.duguje) || 0,
        potrazuje: parseFloat(vals.potrazuje) || 0,
        tip: 'A',
        opis: parentKonto // čuva parent konto za grupisanje
      });
      if (error) throw error;
      toast('Analitika dodana pod ' + parentKonto);
      await loadPocetnoStanje();
    } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
  });
}

function editPocetnoStanje(p) {
  openEditModal('Izmeni: ' + (p.konto || '').trim() + ' ' + (p.naziv_konta || '').substring(0, 20), [
    { label: 'Konto', value: p.konto || '', key: 'konto' },
    { label: 'Naziv konta', value: p.naziv_konta || '', key: 'naziv_konta' },
    { label: 'Duguje', value: p.duguje || '0', key: 'duguje', type: 'number', step: '0.01' },
    { label: 'Potražuje', value: p.potrazuje || '0', key: 'potrazuje', type: 'number', step: '0.01' }
  ], (vals) => {
    const dug = parseFloat(vals.duguje) || 0;
    const pot = parseFloat(vals.potrazuje) || 0;
    const konto = (vals.konto || '').trim();
    const naziv = (vals.naziv_konta || '').trim();
    sb.from('pocetno_stanje').update({ konto, naziv_konta: naziv, duguje: dug, potrazuje: pot }).eq('id', p.id)
      .then(({ error }) => {
        if (error) { toast('Greška: ' + error.message, 'err'); return; }
        p.konto = konto; p.naziv_konta = naziv; p.duguje = dug; p.potrazuje = pot;
        const bp = basePsData.find(x => x.id === p.id);
        if (bp) { bp.konto = konto; bp.naziv_konta = naziv; bp.duguje = dug; bp.potrazuje = pot; }
        renderPocetnoStanje();
        toast('Ažurirano');
      });
  });
}

function deletePocetnoStanje(p) {
  showConfirm('Obrisati "' + (p.konto || '').trim() + ' ' + (p.naziv_konta || '').trim() + '"?', () => {
    sb.from('pocetno_stanje').delete().eq('id', p.id)
      .then(({ error }) => {
        if (error) { toast('Greška: ' + error.message, 'err'); return; }
        psData = psData.filter(x => x.id !== p.id);
        basePsData = basePsData.filter(x => x.id !== p.id);
        renderPocetnoStanje();
        toast('Stavka obrisana');
      });
  });
}

export function deleteSelectedPs() {
  const checked = [...document.querySelectorAll('.ps-ck:checked')].map(c => parseInt(c.value));
  if (!checked.length) return;
  const msg = checked.length === psData.filter(p => p.tip !== 'X').length
    ? 'OBRISATI SVE STAVKE POČETNOG STANJA (' + checked.length + ')? Ova akcija je NEPOVRATNA!'
    : 'Obrisati ' + checked.length + ' selektovanih stavki? Ova akcija je nepovratna.';
  showConfirm(msg, async () => {
    showLoading();
    try {
      const { error } = await sb.from('pocetno_stanje').delete().in('id', checked);
      if (error) throw error;
      basePsData = basePsData.filter(x => !checked.includes(x.id));
      psData = basePsData;
      buildPsKontoDropdown();
      renderPocetnoStanje();
      toast(checked.length + ' stavki obrisano');
    } catch (e) { toast('Greška: ' + (e.message || e), 'err'); }
    hideLoading();
  });
}

// ============================================================
// IMPORT
// ============================================================
export function handlePsImport(inp) {
  if (!inp.files.length) return;
  const f = inp.files[0]; inp.value = '';
  const r = new FileReader();
  r.onload = async (e) => {
    try {
      showLoading();
      const wb = XLSX.read(e.target.result, { type: 'array', raw: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true });
      if (rows.length < 2) { hideLoading(); toast('Prazan fajl', 'err'); return; }
      const data = rows.slice(1).filter(r => r[0] != null && r[0] !== '').map(r => ({
        konto: String(r[0] || '').trim(), naziv_konta: String(r[1] || '').trim(),
        duguje: parseFloat(r[2]) || 0, potrazuje: parseFloat(r[3]) || 0,
        broj_racuna: String(r[4] || '').trim(), datum_racuna: String(r[5] || '').trim(),
        opis: String(r[6] || '').trim(), tip: String(r[7] || 'S').trim()
      }));
      if (!data.length) { hideLoading(); toast('Nema podataka', 'err'); return; }
      const { data: count, error } = await sb.rpc('import_pocetno_stanje', { p_data: data });
      if (error) throw error;
      hideLoading();
      toast('Importovano ' + count + ' stavki');
      loadPocetnoStanje();
    } catch (e) { hideLoading(); toast('Greška: ' + (e.message || e), 'err'); }
  };
  r.readAsArrayBuffer(f);
}

// ============================================================
// EXPORT
// ============================================================
export function expPsQuickBooks() {
  if (!psData.length) { toast('Nema podataka', 'err'); return; }
  const wb = XLSX.utils.book_new();
  const hdr = ['*JournalNo', '*JournalDate', 'AccountCode', '*AccountName', 'Debits', 'Credits', 'Description'];
  const rows = [hdr];
  const summaryRows = psData.filter(p => p.tip === 'S');
  const dateStr = new Date().toISOString().slice(0, 10);
  summaryRows.forEach((p, i) => {
    rows.push(['OB-' + (i + 1), dateStr, (p.konto || '').trim(), (p.naziv_konta || '').trim(),
      parseFloat(p.duguje || 0) || '', parseFloat(p.potrazuje || 0) || '', 'Početno stanje - ' + (p.naziv_konta || '').trim()]);
  });
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 40 }, { wch: 16 }, { wch: 16 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Journal Entries');
  XLSX.writeFile(wb, 'quickbooks_opening_balance.xlsx');
  if (state.CU) sb.rpc('log_audit', { p_user_id: state.CU.id, p_action: 'export_quickbooks', p_entity_type: 'pocetno_stanje', p_entity_name: 'quickbooks_opening_balance.xlsx' }).catch(() => {});
  toast('Export kreiran (' + summaryRows.length + ' stavki)');
}

// ============================================================
// ZAKLJUČI PERIOD
// ============================================================
export function openClosePeriod() {
  const now = new Date();
  const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  document.getElementById('cpMonth').value = prevMonth;
  document.getElementById('cpYear').value = prevYear;
  document.getElementById('cpPreview').style.display = 'none';
  document.getElementById('mdlClosePeriod').classList.add('active');
}

export async function confirmClosePeriod() {
  const month = parseInt(document.getElementById('cpMonth').value);
  const year = parseInt(document.getElementById('cpYear').value);
  if (!month || !year) { toast('Izaberite mesec i godinu', 'err'); return; }
  const monthNames = ['', 'januar', 'februar', 'mart', 'april', 'maj', 'jun', 'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'];
  showConfirm('Zaključiti ' + monthNames[month] + ' ' + year + '? Ova akcija je nepovratna!', async () => {
    showLoading();
    try {
      const { data, error } = await sb.rpc('close_period', { p_user_id: state.CU.id, p_year: year, p_month: month });
      if (error) throw error;
      clMdl('mdlClosePeriod');
      hideLoading();
      toast('Period ' + monthNames[month] + ' ' + year + ' zaključen.');
      loadPocetnoStanje();
    } catch (e) { hideLoading(); toast('Greška: ' + (e.message || e), 'err'); }
  });
}

export const debounceRenderPS = (() => { let t; return () => { clearTimeout(t); t = setTimeout(renderPocetnoStanje, 300); }; })();
